import {
  dispositionCheckboxOptions,
  DispositionDTO,
  DispositionFollowUpType,
  DispositionType,
  followUpInOptions,
  NOTHING_TO_EAT_OR_DRINK_FIELD,
} from 'utils';

export const dispositionFieldsPerType: { [key in DispositionType]: string[] } = {
  pcp: ['followUpIn', 'followUpType'],
  ip: ['bookVisit'],
  'ip-lab': ['labService', 'bookVisit', 'followUpType'],
  ed: [NOTHING_TO_EAT_OR_DRINK_FIELD],
  'ip-oth': [],
  'pcp-no-type': ['followUpIn'],
  another: ['reason'],
  specialty: ['followUpIn'],
};

export const DEFAULT_DISPOSITION_VALUES: DispositionFormValues = {
  type: 'pcp-no-type',
  note: '',
  followUpIn: '',
  reason: '',
  labService: [],
  virusTest: [],
  dentistry: false,
  ent: false,
  ophthalmology: false,
  orthopedics: false,
  other: false,
  'lurie-ct': false,
  otherNote: '',
  [NOTHING_TO_EAT_OR_DRINK_FIELD]: false,
};

export type DispositionFormValues = Pick<DispositionDTO, 'type' | 'note' | typeof NOTHING_TO_EAT_OR_DRINK_FIELD> & {
  [key in DispositionFollowUpType]: boolean;
} & {
  otherNote: string;
  followUpIn: number | '';
  labService: string[];
  virusTest: string[];
  reason: string;
};

export const mapFormToDisposition = (values: DispositionFormValues): DispositionDTO => {
  const disposition: DispositionDTO = { type: values.type, note: values.note || 'N/A' };

  const fields = dispositionFieldsPerType[disposition.type];

  if (fields.includes('labService')) {
    disposition.labService = values.labService || [];
    disposition.virusTest = values.virusTest || [];
  }

  if (fields.includes('followUpIn')) {
    disposition.followUpIn = values.followUpIn === '' ? undefined : values.followUpIn;
  }

  if (fields.includes('reason')) {
    disposition.reason = values.reason === '' ? undefined : values.reason;
  }

  if (fields.includes('followUpType')) {
    disposition.followUp = [];

    dispositionCheckboxOptions.forEach((option) => {
      const isOtherOption = option.name === 'other';
      const otherOptionValue = option.name === 'other' ? values.otherNote?.trim?.() : undefined;
      const isOtherOptionWithValue = isOtherOption && otherOptionValue;

      if ((!isOtherOption && values[option.name]) || isOtherOptionWithValue) {
        disposition.followUp!.push({
          type: option.name,
          note: option.name === 'other' ? otherOptionValue : undefined,
        });
      }
    });

    if (disposition.followUp.length === 0) {
      delete disposition.followUp;
    }
  }

  if (fields.includes(NOTHING_TO_EAT_OR_DRINK_FIELD)) {
    disposition[NOTHING_TO_EAT_OR_DRINK_FIELD] = values[NOTHING_TO_EAT_OR_DRINK_FIELD];
  }

  return disposition;
};

export const mapDispositionToForm = (disposition: DispositionDTO): DispositionFormValues => {
  const values = { ...DEFAULT_DISPOSITION_VALUES };

  values.type = disposition.type;
  values.note = disposition.note === 'N/A' ? '' : disposition.note;

  const fields = dispositionFieldsPerType[disposition.type];

  if (fields.includes('labService')) {
    values.labService = disposition.labService || [];
    values.virusTest = disposition.virusTest || [];
  }

  if (fields.includes('followUpIn')) {
    values.followUpIn = typeof disposition.followUpIn === 'number' ? disposition.followUpIn : '';
  }

  if (fields.includes('reason')) {
    values.reason = disposition.reason || '';
  }

  if (fields.includes('followUpType')) {
    disposition.followUp?.forEach((followUp) => {
      values[followUp.type] = true;
      if (followUp.type === 'other') {
        values.otherNote = followUp.note || '';
      }
    });
  }

  if (fields.includes(NOTHING_TO_EAT_OR_DRINK_FIELD)) {
    values[NOTHING_TO_EAT_OR_DRINK_FIELD] = disposition[NOTHING_TO_EAT_OR_DRINK_FIELD];
  }

  return values;
};

export { followUpInOptions };

export const SEND_OUT_VIRUS_TEST_LABEL = 'telemed.plan.disposition.labServiceOptions.send-out-virus-test';

export const labServiceOptions = [
  {
    key: 'covid-pcr',
    label: 'telemed.plan.disposition.labServiceOptions.covid-pcr',
    note: 'telemed.plan.disposition.labServiceNotes.covid-pcr',
  },
  {
    key: 'covid-rapid-antigen',
    label: 'telemed.plan.disposition.labServiceOptions.covid-rapid-antigen',
    note: 'telemed.plan.disposition.labServiceNotes.covid-rapid-antigen',
  },
  {
    key: 'covid-rapid-antigen-reflex',
    label: 'telemed.plan.disposition.labServiceOptions.covid-rapid-antigen-reflex',
    note: 'telemed.plan.disposition.labServiceNotes.covid-rapid-antigen-reflex',
  },
  {
    key: 'multiple-tests',
    label: 'telemed.plan.disposition.labServiceOptions.multiple-tests',
    note: 'telemed.plan.disposition.labServiceNotes.multiple-tests',
  },
  {
    key: 'rapid-strep',
    label: 'telemed.plan.disposition.labServiceOptions.rapid-strep',
    note: 'telemed.plan.disposition.labServiceNotes.rapid-strep',
  },
  {
    key: 'rapid-strep-covid-pcr',
    label: 'telemed.plan.disposition.labServiceOptions.rapid-strep-covid-pcr',
    note: 'telemed.plan.disposition.labServiceNotes.rapid-strep-covid-pcr',
  },
  {
    key: 'ua-ucx',
    label: 'telemed.plan.disposition.labServiceOptions.ua-ucx',
    note: 'telemed.plan.disposition.labServiceNotes.ua-ucx',
  },
  { key: 'send-out-virus-test', label: 'telemed.plan.disposition.labServiceOptions.send-out-virus-test' },
];

export const virusTestsOptions = ['Flu', 'RSV', 'COVID', 'Other'];

export const reasonsForTransferOptions = ['Equipment availability', 'Procedure or advanced care', 'Xray'];
